import React from 'react';

const DROPDOWN_ITEM_CLASS = 'bs4-dropdown-item';
const DROPDOWN_ITEM_SELECTOR = `.${DROPDOWN_ITEM_CLASS}`;
const VERTICAL_ARROW_KEY_NAMES = ['ArrowUp', 'ArrowDown'] as const;

/**
 * Given that some dropdown items (elements that have DROPDOWN_ITEM_CLASS class)
 * from NvDropdown are wrapped by another element we may need to look for it
 * in its children (that's why I use querySelector).
 * Traverses multiple siblings to skip over non-item elements like dividers and headers.
 */
const getRealSiblingDropdownItems = (element: Element) => {
  const getRealDropdownItem = (siblingElement: Element | null): Element | null => {
    if (!siblingElement) return null;
    if (siblingElement.classList.contains(DROPDOWN_ITEM_CLASS)) {
      return siblingElement;
    }

    return siblingElement.querySelector(DROPDOWN_ITEM_SELECTOR);
  };

  const findNextDropdownItem = (sibling: Element | null): Element | null => {
    if (!sibling) return null;
    const item = getRealDropdownItem(sibling);
    if (item) return item;
    return findNextDropdownItem(sibling.nextElementSibling);
  };

  const findPrevDropdownItem = (sibling: Element | null): Element | null => {
    if (!sibling) return null;
    const item = getRealDropdownItem(sibling);
    if (item) return item;
    return findPrevDropdownItem(sibling.previousElementSibling);
  };

  return {
    nextElementSibling: findNextDropdownItem(element.nextElementSibling),
    previousElementSibling: findPrevDropdownItem(element.previousElementSibling),
  };
};

/**
 * Recursive function that returns a list of all focusable children elements
 * of a given element, this is used in useDropdownItemInnerFocusables hook to
 * know what are the elements and order we need to focus them before focusing
 * next dropdown item.
 */
const getFocusableChildrenElements = (element: HTMLElement | null, list = [], isFirstTime = true) => {
  if (element === null) {
    return list;
  }

  if (!isFirstTime && element.tabIndex === 0) {
    list.push(element);
  }

  getFocusableChildrenElements(element.firstElementChild as HTMLElement, list, false);

  if (!isFirstTime) {
    getFocusableChildrenElements(element.nextElementSibling as HTMLElement, list, false);
  }

  return list;
};

export const isVerticalArrowKey = (key: string) => VERTICAL_ARROW_KEY_NAMES.includes(key as VerticalArrowKeyName);

const getDeltaFromArrowKeyName = (verticalArrowKeyName: VerticalArrowKeyName) => {
  if (verticalArrowKeyName === 'ArrowUp') {
    return -1;
  }

  return 1;
};

type VerticalArrowKeyName = typeof VERTICAL_ARROW_KEY_NAMES[number];

/**
 * Hook that provides keyboard accessibility features specifically to NvDropdown
 * component items, you may need to use this hook if you built a custom dropdown
 * item that has focusable elements in it.
 * It allows you to select children focusable elements with up and down keyboard
 * arrow keys.
 * NOTE: This is for strict use with NvDropdown component.
 */
const useDropdownItemInnerFocusables = () => {
  const itemElementRef = React.useRef<HTMLElement>();
  const focusedElementIndexRef = React.useRef<number>();
  const focusablesRef = React.useRef<HTMLElement[]>([]);

  const focusCurrentElementRef = React.useRef(() => {
    const { current: focusables } = focusablesRef;
    const { current: focusedElementIndex } = focusedElementIndexRef;

    focusables[focusedElementIndex]?.focus();
  });

  const handleFocusRef = React.useRef<React.FocusEventHandler<HTMLElement>>((e) => {
    const { current: focusables } = focusablesRef;
    const { current: itemElement } = itemElementRef;

    // Focus moved between inner child elements — don't reset the index.
    if (e.relatedTarget instanceof Node && itemElement.contains(e.relatedTarget)) {
      return;
    }

    if (e.relatedTarget instanceof Node) {
      // Use Range.comparePoint to determine if focus came from above or below in
      // the DOM, regardless of whether relatedTarget is the immediate sibling.
      // This correctly handles cases where dividers/headers sit between items.
      // -1 → relatedTarget is before itemElement (came from above)
      //  1 → relatedTarget is after itemElement (came from below)
      // Wrapped in try/catch because comparePoint throws WrongDocumentError if
      // the node belongs to a different document (e.g. a portal into an iframe).
      try {
        const range = document.createRange();
        range.selectNode(itemElement);
        // Offset 0 is intentional: for element nodes it refers to the position
        // before the first child, which is valid regardless of node type.
        const compareResult = range.comparePoint(e.relatedTarget, 0);
        if (compareResult < 0) {
          focusedElementIndexRef.current = -1;
        } else if (compareResult > 0) {
          // Set index past the end so the first ArrowUp enters the item from the bottom.
          focusedElementIndexRef.current = focusables.length;
        }
      } catch {
        // Cross-document node — fall back to top-entry default.
        focusedElementIndexRef.current = -1;
      }
    } else {
      // No relatedTarget or a non-Node EventTarget (e.g. focus arrived via mouse
      // or programmatic call) — default to -1 so ArrowDown enters from the top.
      focusedElementIndexRef.current = -1;
    }
  });

  const handleKeydownRef = React.useRef<React.KeyboardEventHandler<HTMLElement>>((e) => {
    const key = e.key as VerticalArrowKeyName;
    const { current: focusables } = focusablesRef;
    const { current: itemElement } = itemElementRef;
    const { current: focusCurrentElement } = focusCurrentElementRef;
    const {
      nextElementSibling,
      previousElementSibling,
    } = getRealSiblingDropdownItems(itemElement);

    if (isVerticalArrowKey(key)) {
      e.preventDefault();
      e.stopPropagation();

      const delta = getDeltaFromArrowKeyName(key);
      const newValue = focusedElementIndexRef.current + delta;

      if (
        newValue < focusables.length
        && newValue > -1
      ) {
        focusedElementIndexRef.current = newValue;
        focusCurrentElement();
      } else if (newValue < 0) {
        (previousElementSibling as HTMLElement)?.focus?.();
      } else {
        (nextElementSibling as HTMLElement)?.focus?.();
      }
    }
  });

  React.useEffect(() => {
    const { current: itemElement } = itemElementRef;

    if (itemElement) {
      focusablesRef.current = getFocusableChildrenElements(itemElement);
    }
  }, [itemElementRef]);

  return {
    ref: itemElementRef,
    className: DROPDOWN_ITEM_CLASS,
    onFocus: handleFocusRef.current,
    onKeyDown: handleKeydownRef.current,
  };
};

export default useDropdownItemInnerFocusables;
